import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Marquee } from "@/components/blocks/decor/Decor";
import { Cta } from "@/components/blocks/sections/Cta";
import { PageHero } from "@/components/blocks/sections/PageHero";
import { Section } from "@/components/blocks/sections/Section";
import {
	Overline,
	SectionHeading,
} from "@/components/blocks/sections/SectionHeading";
import { Reveal, RevealGroup, RevealItem } from "@/components/motion/motion";
import { Button } from "@/components/ui/button";
import { useSectionViewTracking } from "@/lib/analytics";
import { useI18n } from "@/lib/i18n/use-i18n";
import { USE_CASE_KEYS, USE_CASES } from "@/lib/use-cases";
import { cn } from "@/lib/utils";
import { USE_CASE_SCENES } from "./UseCasePage";

const SECTION_IDS = { list: "situations", cta: "contact" } as const;
const SECTIONS = Object.values(SECTION_IDS).map((id) => ({ id, label: id }));

/** Index of the use cases: one editorial row per trade, with its scene. */
export function UseCasesPage() {
	const { dict, locale, localeParams } = useI18n();
	useSectionViewTracking(SECTIONS, { pageType: "use_case_index", locale });
	const page = dict.useCases.index;
	const items = dict.useCases.items;

	return (
		<>
			<PageHero
				label={page.hero.label}
				title={page.hero.title}
				emphasis={page.hero.emphasis}
				intro={page.hero.intro}
				footer={
					<div className="border-t py-6">
						<Marquee items={USE_CASE_KEYS.map((key) => items[key].card.name)} />
					</div>
				}
			/>

			<Section id={SECTION_IDS.list}>
				<SectionHeading
					index="01"
					label={page.list.label}
					title={page.list.title}
					description={page.list.description}
				/>
				<ol className="mt-16 space-y-6 md:mt-20">
					{USE_CASE_KEYS.map((key, index) => {
						const card = items[key].card;
						const Scene = USE_CASE_SCENES[key];
						return (
							<Reveal
								as="li"
								key={key}
								className="grid overflow-hidden rounded-[2rem] border bg-card lg:grid-cols-2"
							>
								<div
									className={cn(
										"flex flex-col gap-6 p-8 md:p-12",
										index % 2 === 1 && "lg:order-2",
									)}
								>
									<Overline index={String(index + 1).padStart(2, "0")}>
										{card.name}
									</Overline>
									<h2 className="text-[clamp(1.6rem,2.6vw,2.25rem)] leading-[1.1] font-semibold tracking-[-0.045em] text-balance">
										{card.title}
									</h2>
									<p className="text-lg text-pretty text-muted-foreground">
										{card.summary}
									</p>
									<dl className="border-t pt-5 text-sm">
										<dt className="font-mono text-[11px] tracking-[0.07em] text-muted-foreground uppercase">
											{page.audienceLabel}
										</dt>
										<dd className="mt-1.5 text-base">{card.audience}</dd>
									</dl>
									<div className="mt-auto pt-2">
										<Button
											size="lg"
											className="group/btn h-auto min-h-11 rounded-lg px-6 font-mono text-[13px] whitespace-normal"
											asChild
										>
											<Link to={USE_CASES[key].to} params={localeParams}>
												{page.link}
												<ArrowRight className="transition-transform group-hover/btn:translate-x-1" />
											</Link>
										</Button>
									</div>
								</div>
								<div className="dot-grid flex items-center justify-center bg-muted/70 px-6 py-10 md:px-12">
									<Scene className="max-w-[30rem]" />
								</div>
							</Reveal>
						);
					})}
				</ol>

				<RevealGroup className="mt-16 grid gap-10 border-t pt-12 md:grid-cols-2 md:gap-16">
					{[page.note, page.other].map((block) => (
						<RevealItem key={block.title} className="space-y-3">
							<h2 className="text-2xl font-semibold tracking-[-0.025em]">
								{block.title}
							</h2>
							<p className="text-pretty text-muted-foreground">
								{block.description}
							</p>
						</RevealItem>
					))}
				</RevealGroup>
			</Section>

			<Cta id={SECTION_IDS.cta} {...page.cta} placement="use_cases_cta" />
		</>
	);
}
